import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API } from "../lib/api";
import { formatIDR, formatDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Printer, ArrowLeft, Languages } from "lucide-react";
import PondokIcon from "../components/PondokIcon";
import { Link } from "react-router-dom";
import { inferLanguage } from "../lib/whatsapp";

// Bilingual dictionary
const T = {
  id: {
    backToBookings: "Kembali ke Bookings",
    print: "Print / Save as PDF",
    invoice: "Invoice",
    date: "Tanggal",
    billedTo: "Ditagihkan kepada",
    platform: "Platform",
    stayDetails: "Detail Menginap",
    checkIn: "Check-in",
    checkOut: "Check-out",
    nights: "Malam",
    guests: "Tamu",
    guestUnit: "orang",
    desc: "Deskripsi",
    qty: "Qty",
    pricePerNight: "Harga/Malam",
    total: "Total",
    stayDesc: (n, g) => `Menginap ${n} malam untuk ${g} tamu`,
    subtotal: "Subtotal",
    depositPaid: "DP Terbayar",
    balanceDue: "Sisa Tagihan",
    paymentInfo: "Info Pembayaran",
    notes: "Catatan",
    thanksFooter: (h) => `Terima kasih telah memilih ${h}.`,
    bookingStatus: "Status booking",
    formatDate: (iso) => formatDate(iso),
    loadingInvoice: "Memuat invoice…",
    invoiceNotFound: "Invoice tidak ditemukan.",
  },
  en: {
    backToBookings: "Back to Bookings",
    print: "Print / Save as PDF",
    invoice: "Invoice",
    date: "Date",
    billedTo: "Billed To",
    platform: "Platform",
    stayDetails: "Stay Details",
    checkIn: "Check-in",
    checkOut: "Check-out",
    nights: "Nights",
    guests: "Guests",
    guestUnit: "",
    desc: "Description",
    qty: "Qty",
    pricePerNight: "Rate/Night",
    total: "Total",
    stayDesc: (n, g) => `${n} night${n > 1 ? "s" : ""} for ${g} guest${g > 1 ? "s" : ""}`,
    subtotal: "Subtotal",
    depositPaid: "Deposit Paid",
    balanceDue: "Balance Due",
    paymentInfo: "Payment Info",
    notes: "Notes",
    thanksFooter: (h) => `Thank you for choosing ${h}.`,
    bookingStatus: "Booking status",
    formatDate: (iso) => {
      if (!iso) return "-";
      const d = new Date(iso);
      return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    },
    loadingInvoice: "Loading invoice…",
    invoiceNotFound: "Invoice not found.",
  },
};

export default function Invoice() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roomPhoto, setRoomPhoto] = useState(null);
  const [langOverride, setLangOverride] = useState(null);

  useEffect(() => {
    api.get(`/invoices/${id}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [id]);

  const bookingRoomName = data?.booking?.room_name;
  useEffect(() => {
    if (!bookingRoomName) return;
    api.get(`/rooms`).then((r) => {
      const room = r.data.find((x) => x.name === bookingRoomName);
      if (room?.photos?.[0]) {
        const token = localStorage.getItem("meos_token");
        setRoomPhoto(`${API}/photos/${room.id}/${room.photos[0].id}?auth=${token}`);
      }
    }).catch(() => {});
  }, [bookingRoomName]);

  // Compute language: explicit override > booking.language > platform inference
  const lang = langOverride || (data?.booking ? inferLanguage(data.booking) : "id");
  const t = T[lang];

  if (loading) return <div className="p-12 text-[#586D61]">{T.id.loadingInvoice}</div>;
  if (!data) return <div className="p-12 text-[#D14D4D]">{T.id.invoiceNotFound}</div>;

  const { booking, settings, invoice_no, nightly_rate, subtotal, deposit_paid, balance_due } = data;

  return (
    <div className="min-h-screen bg-[#FDFBF7] print:bg-white">
      {/* Toolbar (hidden when printing) */}
      <div className="print:hidden sticky top-0 z-10 border-b border-[#EBE6DE] bg-white/80 backdrop-blur px-8 py-4 flex items-center justify-between">
        <Link to="/bookings" className="flex items-center gap-2 text-[#586D61] hover:text-[#172E21] text-sm">
          <ArrowLeft className="h-4 w-4" /> {t.backToBookings}
        </Link>
        <div className="flex items-center gap-2">
          <Button
            data-testid="btn-toggle-invoice-lang"
            onClick={() => setLangOverride(lang === "id" ? "en" : "id")}
            variant="outline"
            className="border-[#EBE6DE] hover:border-[#D16B4D] hover:text-[#D16B4D] rounded-xl gap-2"
          >
            <Languages className="h-4 w-4" />
            {lang === "id" ? "🇬🇧 English" : "🇮🇩 Indonesia"}
          </Button>
          <Button
            data-testid="btn-print-invoice"
            onClick={() => window.print()}
            className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl"
          >
            <Printer className="h-4 w-4 mr-2" /> {t.print}
          </Button>
        </div>
      </div>

      {/* Invoice paper */}
      <div className="max-w-3xl mx-auto my-8 bg-white border border-[#EBE6DE] rounded-2xl shadow-sm p-10 print:border-0 print:shadow-none print:rounded-none print:my-0 print:max-w-full">
        {/* Header */}
        <div className="flex items-start justify-between gap-6 border-b border-[#EBE6DE] pb-6">
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 rounded-2xl bg-[#D16B4D] flex items-center justify-center">
              <PondokIcon className="h-7 w-7 text-white" strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-heading text-2xl font-medium text-[#172E21]">{settings.homestay_name}</div>
              <div className="text-sm text-[#586D61] mt-1">{settings.homestay_address}</div>
              {settings.homestay_phone && <div className="text-sm text-[#586D61]">{settings.homestay_phone}</div>}
              {settings.homestay_email && <div className="text-sm text-[#586D61]">{settings.homestay_email}</div>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D16B4D]">{t.invoice}</div>
            <div className="font-mono text-lg mt-1" data-testid="invoice-number">{invoice_no}</div>
            <div className="text-xs text-[#586D61] mt-2">{t.date}: {t.formatDate(data.issued_at)}</div>
          </div>
        </div>

        {/* Bill To */}
        <div className="grid grid-cols-2 gap-6 mt-6">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#586D61]">{t.billedTo}</div>
            <div className="font-heading text-lg mt-1 text-[#172E21]">{booking.guest_name}</div>
            {booking.phone_number && <div className="text-sm text-[#586D61] font-mono">{booking.phone_number}</div>}
            <div className="text-xs text-[#586D61] mt-1">{t.platform}: {booking.platform}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#586D61]">{t.stayDetails}</div>
            <div className="grid grid-cols-2 gap-2 mt-1 text-sm">
              <div className="text-[#586D61]">{t.checkIn}</div>
              <div className="text-[#172E21] font-medium text-right">{t.formatDate(booking.check_in)}</div>
              <div className="text-[#586D61]">{t.checkOut}</div>
              <div className="text-[#172E21] font-medium text-right">{t.formatDate(booking.check_out)}</div>
              <div className="text-[#586D61]">{t.nights}</div>
              <div className="text-[#172E21] font-medium text-right">{booking.nights}</div>
              <div className="text-[#586D61]">{t.guests}</div>
              <div className="text-[#172E21] font-medium text-right">{booking.guests}{t.guestUnit ? ` ${t.guestUnit}` : ""}</div>
            </div>
          </div>
        </div>

        {/* Line items */}
        {roomPhoto && (
          <div className="mt-6 rounded-xl overflow-hidden border border-[#EBE6DE] max-h-48">
            <img src={roomPhoto} alt={booking.room_name} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="mt-6 rounded-xl border border-[#EBE6DE] overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#FDFBF7]">
              <tr>
                <th className="text-left px-5 py-3 text-[10px] font-bold uppercase tracking-wider text-[#586D61]">{t.desc}</th>
                <th className="text-center px-5 py-3 text-[10px] font-bold uppercase tracking-wider text-[#586D61]">{t.qty}</th>
                <th className="text-right px-5 py-3 text-[10px] font-bold uppercase tracking-wider text-[#586D61]">{t.pricePerNight}</th>
                <th className="text-right px-5 py-3 text-[10px] font-bold uppercase tracking-wider text-[#586D61]">{t.total}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-[#EBE6DE]">
                <td className="px-5 py-4">
                  <div className="font-medium text-[#172E21]">{booking.room_name}</div>
                  <div className="text-xs text-[#586D61] mt-0.5">{t.stayDesc(booking.nights, booking.guests)}</div>
                </td>
                <td className="px-5 py-4 text-center font-mono">{booking.nights}</td>
                <td className="px-5 py-4 text-right font-mono text-sm">{formatIDR(nightly_rate)}</td>
                <td className="px-5 py-4 text-right font-mono font-medium">{formatIDR(subtotal)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="mt-6 flex justify-end">
          <div className="w-full max-w-xs flex flex-col gap-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[#586D61]">{t.subtotal}</span>
              <span className="font-mono text-[#172E21]">{formatIDR(subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#586D61]">{t.depositPaid}</span>
              <span className="font-mono text-[#2C7A51]">− {formatIDR(deposit_paid)}</span>
            </div>
            <div className="flex justify-between border-t border-[#EBE6DE] pt-3 mt-1 text-base">
              <span className="font-heading font-medium text-[#172E21]">{t.balanceDue}</span>
              <span className="font-mono font-bold text-[#D16B4D]" data-testid="invoice-balance-due">{formatIDR(balance_due)}</span>
            </div>
          </div>
        </div>

        {/* Payment info + notes */}
        {(settings.bank_info || booking.notes) && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {settings.bank_info && (
              <div className="bg-[#FDFBF7] rounded-xl p-4 border border-[#EBE6DE]">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#586D61]">{t.paymentInfo}</div>
                <div className="text-sm mt-2 whitespace-pre-line text-[#172E21]">{settings.bank_info}</div>
              </div>
            )}
            {booking.notes && (
              <div className="bg-[#FDFBF7] rounded-xl p-4 border border-[#EBE6DE]">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#586D61]">{t.notes}</div>
                <div className="text-sm mt-2 whitespace-pre-line text-[#172E21]">{booking.notes}</div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-[#EBE6DE] text-center text-xs text-[#586D61]">
          {t.thanksFooter(settings.homestay_name)} <br />
          {t.bookingStatus}: <span className="font-bold uppercase tracking-wider text-[#D16B4D]">{booking.status}</span>
        </div>
      </div>

      <style>{`
        @media print {
          @page { margin: 16mm; }
          body { background: white !important; }
        }
      `}</style>
    </div>
  );
}
